import {
    HeaderLeft,
    HeaderRow,
    HeaderWrapper,
    InvoiceAmountBox,
    InvoiceNavigationCard,
    NavigationHeader,
    RightAlignedText,
    SmallerHeaderRow,
    SmallInvoiceText,
    InvoiceNavigationControls,
    InvoiceNavigationInfo,
    InvoiceNavigationButtonsContainer,
    InvoiceNavigationRow,
} from '../InvoiceDetail.StyledComponents';
import { Button, ButtonVariants, Icon, IconNames, MessagePosition, ToastMessages, MessageType } from '@triumph/design-system';
import { FlexRowDiv } from '@/app/shared/common.StyledComponents';
import { PageRoutes } from '@/app/components/router/routes';
import NavigateToPageLink from '@/app/shared/components/StyledReactRouterLink/NavigateToPageLink';
import { formatMoney } from '@/app/shared/utils/tableUtils';
import { useTranslation } from 'react-i18next';
import { InvoiceDetailNavigationCardProps } from '../invoiceDetail.types';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { UpdatePaymentStatusModal } from '@/app/shared/components/UpdatePaymentStatusModal/UpdatePaymentStatusModal';
import { useState } from 'react';
import { updateDebtorFeedback } from '@/app/shared/api/endpoints/updateDebtorFeedback';
import { ToastMessageProps } from '@triumph/design-system/components/ToastMessages/ToastMessage/ToastMessage.types';

export const InvoiceDetailNavigationCard: React.FC<InvoiceDetailNavigationCardProps> = ({
    invoiceDetails,
    hasMultipleSelected = false,
    hasPrevious = false,
    hasNext = false,
    onPrevious,
    onNext,
    currentIndex = 0,
    totalSelected = 0,
}) => {
    const { t } = useTranslation('invoicesDashboard');
    const { theme } = useAppContext();
    const [isPayDateModalOpen, setPayDateModalState] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showMessage, setShowMessage] = useState(false);

    const messages: ToastMessageProps[] = [{
        handleClose: () => reset(),
        type: MessageType.success,
        message: t("toastMessages.submitSuccess"),
        show: showMessage,
        messageId: 1,
        toastTestId: 'invoiceDetailsUpdateSubmittedMessage',
        autoCloseTime: 2000
    }];

    const reset = () => {
        setShowMessage(false);
    };

    const handleBulkPayDateSubmit = async (invoiceNumbers: string[], selectedPaymentDate: string, note: string = '') => {
        setIsSubmitting(true);

        const formattedRows = (invoiceDetails: any) => {
            return {
                invoiceId: invoiceDetails.invoiceId,
                willPay: true,
                disputedReasonId: null,
                userText: note,
                paymentDate: selectedPaymentDate,
                urgent: invoiceDetails.urgent ?? false,
            };
        };

        await updateDebtorFeedback([formattedRows(invoiceDetails)])
            .then(() => {
                setShowMessage(true);
            })
            .catch((error) => {
                return Promise.reject(t("submission.error"));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }

    const handleBulkWillNotPaySubmit = async (invoiceNumbers: string[], disputedReasonId: string, disputedReasonDesc: string, note: string) => {
        setIsSubmitting(true);

        const formattedRows = (invoiceDetails: any) => {
            return {
                invoiceId: invoiceDetails.invoiceId,
                willPay: false,
                disputedReasonId: disputedReasonId,
                userText: note,
                paymentDate: null,
                urgent: invoiceDetails.urgent ?? false,
            };
        };

        await updateDebtorFeedback([formattedRows(invoiceDetails)])
            .then(() => {
                setShowMessage(true);
            })
            .catch((error) => {
                return Promise.reject(t("submission.error"));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }

    return (
        <InvoiceNavigationCard>
            <NavigationHeader>
                <InvoiceNavigationRow>
                    <div>
                        <NavigateToPageLink
                            pageRoute={PageRoutes.DASHBOARD}
                            linkText={`Dashboard`}
                            dataTestId={'dashboard-link-invoice-detail'} />
                        {`> Invoice ` + invoiceDetails?.invoiceNumber}
                    </div>

                    {hasMultipleSelected && (
                        <InvoiceNavigationControls data-testid="invoice-navigation-controls">
                            <InvoiceNavigationInfo data-testid="invoice-navigation-info">
                                {t('invoiceDetail.navigation.invoiceCount', {
                                    current: currentIndex + 1,
                                    total: totalSelected,
                                })}
                            </InvoiceNavigationInfo>
                            <InvoiceNavigationButtonsContainer>
                                <Button
                                    variant={ButtonVariants.secondary}
                                    disabled={!hasPrevious}
                                    onClick={onPrevious}
                                    data-testid="previous-invoice-button"
                                >
                                    {t('invoiceDetail.navigation.previous')}
                                </Button>
                                <Button
                                    variant={ButtonVariants.secondary}
                                    disabled={!hasNext}
                                    onClick={onNext}
                                    data-testid="next-invoice-button"
                                >
                                    {t('invoiceDetail.navigation.next')}
                                </Button>
                            </InvoiceNavigationButtonsContainer>
                        </InvoiceNavigationControls>
                    )}
                </InvoiceNavigationRow>
            </NavigationHeader>
            <HeaderWrapper>
                <HeaderRow>
                    <HeaderLeft>
                        {t("invoiceDetail.invoiceHeader")} {invoiceDetails?.invoiceNumber}
                    </HeaderLeft>
                </HeaderRow>
            </HeaderWrapper>
            <InvoiceAmountBox>
                <FlexRowDiv>
                    <Icon
                        color={theme.colors.greyscale.gray30}
                        name={IconNames.Filetype_Invoice}
                        id={'file-type-invoice-icon'}
                        size={'md'} />
                    {formatMoney(invoiceDetails?.amount)}
                </FlexRowDiv>
                <SmallInvoiceText>
                    {invoiceDetails?.urgent ?
                        t("invoiceDetail.pastDueMessage") :
                        t("invoiceDetail.invoiceAmount")}
                </SmallInvoiceText>
            </InvoiceAmountBox>
            <br />
            <FlexRowDiv>
                <SmallerHeaderRow>
                    {t("invoiceDetail.statusHeader")}
                    <RightAlignedText>{invoiceDetails?.status}</RightAlignedText>
                </SmallerHeaderRow>
            </FlexRowDiv>
            <FlexRowDiv>
                <SmallerHeaderRow>
                    {t("invoiceDetail.ageHeader")}
                    <RightAlignedText>{invoiceDetails?.age + t("invoiceDetail.age")}</RightAlignedText>
                </SmallerHeaderRow>
            </FlexRowDiv>
            <FlexRowDiv>
                <SmallerHeaderRow>
                    {t("invoiceDetail.transactionIdHeader")}
                    <RightAlignedText>{invoiceDetails?.invoiceId}</RightAlignedText>
                </SmallerHeaderRow>
                <RightAlignedText>
                    <Button
                        onClick={() => { setPayDateModalState(true); }}
                    >
                        {t("invoiceDetail.updateInvoiceButton")}
                    </Button>
                    <UpdatePaymentStatusModal
                        isVisible={isPayDateModalOpen}
                        handleClose={() => { setIsSubmitting(false); setPayDateModalState(false); }}
                        selectedInvoices={invoiceDetails ? new Set<string>([invoiceDetails.invoiceNumber]) : new Set<string>()}
                        onBulkPaymentDateChange={handleBulkPayDateSubmit}
                        onBulkWillNotPayChange={handleBulkWillNotPaySubmit}
                        isSubmitting={isSubmitting}
                    />
                </RightAlignedText>
            </FlexRowDiv>
            <ToastMessages messages={messages} position={MessagePosition.bottomLeft} />
        </InvoiceNavigationCard>
    );
};
